/**
 * Get API base URL from environment variable
 */
const API_BASE_URL = import.meta.env.VITE_API_URL || '';

/**
 * Generate full download URL for a file
 * @param fileUrl - The file URL from the API (could be relative or absolute)
 * @returns Full URL with API host
 */
export const getFileDownloadUrl = (fileUrl: string): string => {
  // If the URL already includes the host, return as is
  if (fileUrl.startsWith('http://') || fileUrl.startsWith('https://')) {
    return fileUrl;
  }

  // If it's a relative URL, prepend the API base URL
  if (fileUrl.startsWith('/')) {
    return `${API_BASE_URL}${fileUrl}`;
  }

  // If it doesn't start with /, add it
  return `${API_BASE_URL}/${fileUrl}`;
};

/**
 * Download a file using browser's download functionality
 * @param fileUrl - The file URL (will be converted to full URL)
 * @param fileName - The name for the downloaded file
 */
export const downloadFile = (fileUrl: string, fileName: string): void => {
  const link = document.createElement('a');
  link.href = getFileDownloadUrl(fileUrl);
  link.download = fileName;
  link.target = '_blank';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

/**
 * Format file size for display
 * @param bytes - File size in bytes
 * @returns Formatted string (e.g., "1.5 MB")
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

/**
 * Get file extension from filename
 * @param fileName - The filename
 * @returns File extension (without dot) or empty string
 */
export const getFileExtension = (fileName: string): string => {
  const lastDotIndex = fileName.lastIndexOf('.');
  return lastDotIndex > 0
    ? fileName.substring(lastDotIndex + 1).toLowerCase()
    : '';
};

/**
 * Check if file is an image based on extension
 * @param fileName - The filename
 * @returns True if it's an image file
 */
export const isImageFile = (fileName: string): boolean => {
  const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg', 'webp'];
  const extension = getFileExtension(fileName);
  return imageExtensions.includes(extension);
};
